import { DeleteRegular } from "@fluentui/react-icons";
import { SaveIcon } from "@fluentui/react-icons-mdl2";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import CheckboxField from "components/controls/CheckboxField";
import { TextareaField } from "components/controls/TextareaField";
import { IPackageRowItem } from "components/PackagesView/components/BenefitImplementationPackages/shared";
import { PrimaryButton } from "components/PrimaryButton/PrimaryButton";
import { Flex, RemoveButton } from "components/styled";
import { Form, Formik } from "formik";
import { helpAnchor } from "help/anchor";
import {
  Attachment,
  EAttachmentType,
  EmployeeRowItem,
  ETaxonomy,
  IBenefitPackage,
  IClient,
  IEmployeeEnrollmentBase,
  IRowItem,
  SelectItem,
} from "library/types";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as yup from "yup";
import { AttachmentTypeSelect } from "./AttachmentTypeSelect";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";

function isClient(
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IBenefitPackage
    | IClient
    | IEmployeeEnrollmentBase
): item is IClient {
  return (item as IClient).clientName !== undefined;
}

function isEnrollment(
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IBenefitPackage
    | IClient
    | IEmployeeEnrollmentBase
): item is IEmployeeEnrollmentBase {
  return (item as IEmployeeEnrollmentBase).employeeEnrollmentTag !== undefined;
}

type Props = {
  showIsAttachHiddenForEmployeeField?: boolean;
  attachment: Attachment;
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IBenefitPackage
    | IClient
    | IEmployeeEnrollmentBase;
  updateInProgress: boolean;
  deleteInProgress: boolean;
  onUpdateAttachment: ({
    attachment,
    newValues,
  }: {
    attachment: Attachment;
    newValues: {
      attachmentName: string;
      attachmentSummary: string;
      attachmentAbstract: string;
      isHiddenForEmployee: boolean;
      attachmentType?: EAttachmentType;
    };
  }) => void;
  onDeleteAttachment: (attachment: Attachment) => void;
  showAttachmentTypeSelection?: boolean;
  readOnly?: boolean;
};

export function AttachmentItem({
  attachment,
  item,
  showIsAttachHiddenForEmployeeField = true,
  showAttachmentTypeSelection,
  onUpdateAttachment,
  onDeleteAttachment,
  deleteInProgress,
  updateInProgress,
  readOnly,
}: Props) {
  const { t } = useTranslation();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const attachmentTypeOptions = useTaxonomyOptions(
    ETaxonomy.AttachmentType
  ).filter((option) => option.code !== EAttachmentType.NORMAL); // Exclude employee consent from the list

  const validationSchema = yup.object().shape({
    attachmentName: yup.string().required(),
    attachmentSummary: yup.string().max(4000).nullable(),
    attachmentAbstract: yup.string().max(4000).nullable(),

    // .required("Must have friends") // these constraints are shown if and only if inner constraints are satisfied
    // .min(1, "Minimum of 3 friends"),
  });

  const initialValues = useMemo(() => {
    if (!attachment) return {};
    return {
      attachmentName: attachment.attachmentName,
      attachmentSummary: attachment.attachmentSummary,
      attachmentAbstract: attachment.attachmentAbstract,
      isHiddenForEmployee: attachment.isHiddenForEmployee,
      attachmentType:
        attachmentTypeOptions.find(
          (option) => option.value === attachment.attachmentType
        ) || null,
    };
  }, [attachment, attachmentTypeOptions]);

  const handleSubmit = (values) => {
    onUpdateAttachment({
      attachment,
      newValues: {
        ...values,
        attachmentType: values?.attachmentType?.value || null,
      },
    });
  };

  const toggleDeleteConfirmationState = useCallback(() => {
    setDeleteConfirmationOpen((prev) => !prev);
  }, []);

  // const isManageClientAttachments = isClient(item);
  // const isManageEnrollmentAttachments = isEnrollment(item);

  return (
    <>
      <Formik
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={(values, actions) => {
          handleSubmit(values);
          actions.setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          dirty,

          setFieldValue,
          /* and other goodies */
        }) => {
          return (
            <>
              {(updateInProgress || deleteInProgress) && <AppSpinner />}
              <Form>
                <Container>
                  {showAttachmentTypeSelection && (
                    <AttachmentTypeSelect
                      handleAttachmentTypeChange={(value) => {
                        setFieldValue("attachmentType", value);
                      }}
                      attachmentType={values["attachmentType"] as SelectItem}
                    />
                  )}
                  <TextareaField
                    data-help="attachments-summary"
                    label={t("hbh.documentSummary.label")}
                    name={"attachmentSummary"}
                    value={values["attachmentSummary"] as string}
                    setFieldValue={setFieldValue}
                    error={errors["attachmentSummary"]}
                    rows={3}
                    resize="vertical"
                  />
                  <TextareaField
                    data-help="attachments-abstract"
                    label={t("hbh.documentAbstract.label")}
                    name={"attachmentAbstract"}
                    value={values["attachmentAbstract"] as string}
                    setFieldValue={setFieldValue}
                    error={errors["attachmentAbstract"]}
                    rows={3}
                    resize="vertical"
                  />
                  {showIsAttachHiddenForEmployeeField && (
                    <CheckboxField
                      dataHelp={helpAnchor("attachments-hidden")}
                      name="isHiddenForEmployee"
                      required
                      value={!!values["isHiddenForEmployee"]}
                      setFieldValue={setFieldValue}
                      label={t("hbh.isHiddenForEmployee.label")}
                    />
                  )}
                  {readOnly ? null :<Flex justify="space-between">
                    <RemoveButton
                      data-help="attachments-remove"
                      disabled={updateInProgress || deleteInProgress}
                      onClick={() => {
                        setDeleteConfirmationOpen(true);
                      }}
                      icon={<DeleteRegular />}
                    >
                      {t("hbh.removeDocument.label")}
                    </RemoveButton>
                    <PrimaryButton
                      data-help="attachments-save"
                      disabled={!dirty || updateInProgress || deleteInProgress}
                      icon={<SaveIcon />}
                      style={{
                        paddingLeft: "25px",
                        paddingRight: "25px",
                      }}
                    >
                      {t("hbh.saveDocument.label")}
                    </PrimaryButton>
                  </Flex> }
                </Container>
              </Form>
            </>
          );
        }}
      </Formik>
      {deleteConfirmationOpen && (
        <ConfirmationDialog
          isOpen={deleteConfirmationOpen}
          toggleOpen={toggleDeleteConfirmationState}
          onConfirm={() => {
            onDeleteAttachment(attachment);
            toggleDeleteConfirmationState();
          }}
          description={t("hbh.deleteDocumentConfirm.label")}
          confirmLabel={t("greco.delete")}
          title={`${t("hbh.deleteDocument.label")}: ${
            attachment?.attachmentName
          } `}
        />
      )}
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.palette.neutralLight};
  box-sizing: border-box;
  gap: 10px;
  padding: 10px;
`;
